import { object, picklist, safeParse } from "valibot";
import { resolveWidgetEnvironment } from "./checkout-environment.ts";
import {
  type ParsedCalculateTotalInput,
  type ParsedCheckoutPricingInput,
  parseCalculateTotalInput,
  parseLoadCheckoutInput,
  parsePaymentReferenceInput,
  parseSubmitPaymentInput,
} from "./checkout-input-schemas.ts";
import {
  parseCatalog,
  parseCheckoutQuote,
  parsePaymentResult,
  parsePublicWidgetLoad,
} from "./checkout-schemas.ts";
import {
  assertCheckoutItemsAreAvailable,
  resolveCheckoutSelection,
} from "./checkout-selection.ts";
import { deepFreeze } from "./deep-freeze.ts";
import { createSdkErrorReporter } from "./error-reporting.ts";
import {
  cloneCatalog,
  clonePaymentResult,
  clonePreparedCheckout,
  createCatalog,
} from "./snapshots.ts";
import { createTransport } from "./transport.ts";
import {
  type CreateLockerverseWidgetOptions,
  type LockerverseCalculateTotalInput,
  type LockerverseCatalog,
  type LockerverseCheckoutClient,
  type LockerverseCheckoutItem,
  type LockerverseCheckoutPricingInput,
  type LockerverseCheckoutSelectionItem,
  type LockerverseLoadCheckoutInput,
  type LockerverseLoadedCheckout,
  type LockerversePreparedCheckout,
  LockerverseSdkError,
  type LockerverseSdkOperation,
  type LockerverseSdkServerCode,
  type LockerverseSubmitPaymentInput,
} from "./types.ts";

type OperationErrorContext = {
  code:
    | "payment_status_failed"
    | "payment_submit_failed"
    | "total_calculation_failed"
    | "widget_load_failed";
  message: string;
  operation:
    | "calculate_total"
    | "get_payment_status"
    | "load_widget"
    | "submit_payment";
};

const MAX_SELECTED_WIDGET_URL_LENGTH = 4000;
const paymentServerErrorSchema = object({
  code: picklist([
    "PAYMENT_WIDGET_PHONE_INVALID",
    "PAYMENT_WIDGET_PHONE_REQUIRED",
    "PAYMENT_WIDGET_SALES_LIMIT_REACHED",
  ]),
});

async function readPaymentServerCode(
  response: Response
): Promise<LockerverseSdkServerCode | undefined> {
  if (response.status !== 400 && response.status !== 409) {
    return;
  }
  try {
    const result = safeParse(paymentServerErrorSchema, await response.json());
    return result.success ? result.output.code : undefined;
  } catch {
    // Non-JSON error bodies do not contain a safe server code.
  }
}

function resolveCalculateTotalItems({
  catalog,
  items,
  selection,
}: ParsedCalculateTotalInput) {
  if (items !== undefined && selection !== undefined) {
    throw new TypeError("Use items or selection, not both.");
  }
  const selectionCatalog = cloneCatalog(parseCatalog(catalog));
  const checkoutItems =
    selection === undefined
      ? requireCheckoutItems(items)
      : resolveCheckoutSelection(selectionCatalog, selection).items;
  assertCheckoutItemsAreAvailable(selectionCatalog, checkoutItems);
  return {
    catalog: selectionCatalog,
    items: checkoutItems,
  };
}

function requireCheckoutItems(
  items: ParsedCalculateTotalInput["items"]
): LockerverseCheckoutItem[] {
  if (items === undefined) {
    throw new TypeError("Checkout selection is invalid.");
  }
  return items;
}

export function createLockerverseCheckoutClient(
  options: CreateLockerverseWidgetOptions
): LockerverseCheckoutClient {
  const {
    apiBaseUrl,
    communitySlug,
    communitySlugValue,
    environment,
    fetchImplementation,
    onError,
    requestTimeoutMs,
    sentryDsn,
    widgetSlug,
    widgetSlugValue,
  } = resolveWidgetEnvironment(options);
  const reportError = createSdkErrorReporter({
    communitySlug: communitySlugValue,
    environment,
    getContext: () => ({ widgetSlug: widgetSlugValue }),
    onError,
    resourceContextName: "lockerverse_widget",
    sentryDsn,
  });
  const { requestJson } = createTransport(
    fetchImplementation,
    reportError,
    requestTimeoutMs
  );
  const checkoutBaseUrl = `${apiBaseUrl}/v2/community/${communitySlug}/payment-widgets/${widgetSlug}/checkout`;
  const widgetUrl = `${apiBaseUrl}/v2/community/${communitySlug}/payment-widgets/${widgetSlug}`;

  function invalidInput(
    message: string,
    operation: LockerverseSdkOperation,
    code: "invalid_selection" | "payment_submit_failed" = "invalid_selection"
  ): never {
    throw reportError(
      new LockerverseSdkError({
        code,
        message,
        operation,
        reportable: false,
      })
    );
  }

  function normalizeOperationError<ErrorValue>(
    error: ErrorValue,
    context: OperationErrorContext
  ) {
    return error instanceof LockerverseSdkError
      ? error
      : new LockerverseSdkError({ ...context, reportable: true });
  }

  function failOperation<ErrorValue>(
    error: ErrorValue,
    context: OperationErrorContext,
    recoveryMayBeRequired = false
  ): never {
    const normalized = normalizeOperationError(error, context);
    const recoveryRecommended =
      recoveryMayBeRequired &&
      (normalized.status === undefined || normalized.status >= 500);
    if (!recoveryRecommended) {
      throw reportError(normalized);
    }
    const recoveryError = new LockerverseSdkError({
      code: normalized.code,
      failureKind: normalized.failureKind,
      message: normalized.message,
      operation: normalized.operation,
      recoveryRecommended: true,
      reportable: normalized.reportable,
      status: normalized.status,
    });
    throw reportError(recoveryError);
  }

  function normalizePricingInput(input: ParsedCheckoutPricingInput) {
    const email = input.email?.trim().toLowerCase();
    const promoCode = input.promoCode?.trim();
    const pricing: LockerverseCheckoutPricingInput = {};
    if (email) {
      pricing.email = email;
    }
    if (input.includeServiceFee !== undefined) {
      pricing.includeServiceFee = input.includeServiceFee;
    }
    if (promoCode) {
      pricing.promoCode = promoCode;
    }
    if (input.tipCents !== undefined) {
      pricing.tipCents = input.tipCents;
    }
    return pricing;
  }

  function normalizeLoadSelection(input: LockerverseLoadCheckoutInput) {
    try {
      return parseLoadCheckoutInput(input).selection;
    } catch {
      invalidInput("Checkout selection is invalid.", "load_widget");
    }
  }

  function createPreparedCheckout(
    quote: ReturnType<typeof parseCheckoutQuote>,
    items: LockerverseCheckoutItem[],
    pricing: LockerverseCheckoutPricingInput,
    payment: LockerverseCatalog["payment"]
  ): LockerversePreparedCheckout {
    const { connectedAccountId, ...checkoutQuote } = quote;
    return clonePreparedCheckout(
      deepFreeze({
        ...checkoutQuote,
        authoritative: true as const,
        items,
        payment: {
          connectedAccountId,
          currency: "usd" as const,
          publishableKey: payment.publishableKey,
        },
        paymentOption: quote.paymentOption,
        paymentReference: globalThis.crypto.randomUUID(),
        pricing,
      })
    );
  }

  function resolveSelectedCheckoutItems(
    catalog: LockerverseCatalog,
    selection: LockerverseCheckoutSelectionItem[],
    operation: LockerverseSdkOperation
  ) {
    try {
      return resolveCheckoutSelection(catalog, selection).items;
    } catch (error) {
      if (error instanceof TypeError) {
        invalidInput(error.message, operation);
      }
      throw error;
    }
  }

  function createSelectedCheckout(
    catalog: LockerverseCatalog,
    selection: LockerverseCheckoutSelectionItem[],
    quote: ReturnType<typeof parseCheckoutQuote> | undefined,
    context: OperationErrorContext
  ) {
    if (!quote) {
      throw new LockerverseSdkError({
        ...context,
        message: "Lockerverse did not return the requested initial quote.",
        reportable: true,
      });
    }
    const checkoutItems = resolveSelectedCheckoutItems(
      catalog,
      selection,
      context.operation
    );
    return deepFreeze({
      catalog: cloneCatalog(catalog),
      checkout: createPreparedCheckout(
        quote,
        checkoutItems,
        { includeServiceFee: catalog.widget.fields.serviceFee },
        catalog.payment
      ),
    });
  }

  function requestWidgetResource(): Promise<LockerverseCatalog>;
  function requestWidgetResource(
    selection: LockerverseCheckoutSelectionItem[]
  ): Promise<LockerverseLoadedCheckout>;
  async function requestWidgetResource(
    selection?: LockerverseCheckoutSelectionItem[]
  ): Promise<LockerverseCatalog | LockerverseLoadedCheckout> {
    const context = {
      code: "widget_load_failed" as const,
      message: "Lockerverse returned an invalid widget catalog.",
      operation: "load_widget" as const,
    };
    const requestUrl = selection
      ? `${widgetUrl}?selection=${encodeURIComponent(JSON.stringify(selection))}`
      : widgetUrl;
    const request = () =>
      requestJson(
        requestUrl,
        selection ? { cache: "no-store" } : undefined,
        parsePublicWidgetLoad,
        {
          ...context,
          message:
            "Unable to load Lockerverse widget because the request failed.",
          report: false,
        },
        { ...context, report: false },
        readPaymentServerCode
      );
    async function requestWithRetry() {
      try {
        return await request();
      } catch (error) {
        if (
          !(error instanceof LockerverseSdkError) ||
          (error.failureKind !== "network" && error.failureKind !== "timeout")
        ) {
          throw error;
        }
      }
      // Retry only this public GET. A checkout POST can create an uncertain payment.
      return request();
    }
    const response = await requestWithRetry();
    if (!response.ok) {
      const serverCode = response.errorValue;
      if (serverCode === "PAYMENT_WIDGET_SALES_LIMIT_REACHED") {
        throw new LockerverseSdkError({
          ...context,
          message: "This option is sold out.",
          reportable: false,
          serverCode,
          status: response.response.status,
        });
      }
      throw new LockerverseSdkError({
        ...context,
        message: `Unable to load Lockerverse widget (${response.response.status}).`,
        reportable: response.response.status >= 500,
        status: response.response.status,
      });
    }
    const catalog = deepFreeze(createCatalog(response.value.widget));
    if (!selection) {
      return cloneCatalog(catalog);
    }
    return createSelectedCheckout(
      catalog,
      selection,
      response.value.quote,
      context
    );
  }

  async function loadLargeSelection(
    selection: LockerverseCheckoutSelectionItem[],
    context: OperationErrorContext
  ): Promise<LockerverseLoadedCheckout> {
    const loaded = await requestWidgetResource();
    const pricing = {
      includeServiceFee: loaded.widget.fields.serviceFee,
    };
    const items = resolveSelectedCheckoutItems(
      loaded,
      selection,
      context.operation
    );
    return deepFreeze({
      catalog: loaded,
      checkout: await requestCheckoutQuote(
        items,
        pricing,
        loaded.payment,
        context
      ),
    });
  }

  async function loadWidget(
    selection?: LockerverseCheckoutSelectionItem[]
  ): Promise<LockerverseCatalog | LockerverseLoadedCheckout> {
    const context = {
      code: "widget_load_failed" as const,
      message: "Lockerverse returned an invalid widget catalog.",
      operation: "load_widget" as const,
    };
    try {
      if (
        selection &&
        `${widgetUrl}?selection=${encodeURIComponent(JSON.stringify(selection))}`
          .length > MAX_SELECTED_WIDGET_URL_LENGTH
      ) {
        return await loadLargeSelection(selection, context);
      }
      return selection
        ? await requestWidgetResource(selection)
        : await requestWidgetResource();
    } catch (error) {
      failOperation(error, context);
    }
  }

  function load(): Promise<LockerverseCatalog>;
  function load(
    input: LockerverseLoadCheckoutInput
  ): Promise<LockerverseLoadedCheckout>;
  function load(input?: LockerverseLoadCheckoutInput) {
    return input === undefined
      ? loadWidget()
      : loadWidget(normalizeLoadSelection(input));
  }

  async function requestCheckoutQuote(
    items: LockerverseCheckoutItem[],
    pricing: LockerverseCheckoutPricingInput,
    payment: LockerverseCatalog["payment"],
    context: OperationErrorContext
  ) {
    const response = await requestJson(
      `${checkoutBaseUrl}/quote`,
      {
        body: JSON.stringify({ ...pricing, items }),
        headers: { "Content-Type": "application/json" },
        method: "POST",
      },
      parseCheckoutQuote,
      {
        ...context,
        message:
          "Unable to calculate the Lockerverse checkout total because the request failed.",
        report: false,
      },
      { ...context, report: false },
      readPaymentServerCode
    );
    if (!response.ok) {
      const serverCode = response.errorValue;
      if (serverCode === "PAYMENT_WIDGET_SALES_LIMIT_REACHED") {
        throw new LockerverseSdkError({
          ...context,
          message: "This option is sold out.",
          reportable: false,
          serverCode,
          status: response.response.status,
        });
      }
      throw new LockerverseSdkError({
        ...context,
        message: `Unable to calculate the Lockerverse checkout total (${response.response.status}).`,
        reportable: response.response.status >= 500,
        status: response.response.status,
      });
    }
    return createPreparedCheckout(response.value, items, pricing, payment);
  }

  async function calculateTotal(input: LockerverseCalculateTotalInput) {
    const { catalog, items, pricing } = normalizeCalculateTotalInput(input);
    const context = {
      code: "total_calculation_failed" as const,
      message: "Lockerverse returned an invalid checkout total.",
      operation: "calculate_total" as const,
    };
    try {
      return await requestCheckoutQuote(
        items,
        pricing,
        catalog.payment,
        context
      );
    } catch (error) {
      failOperation(error, context);
    }
  }

  function normalizeCalculateTotalInput(input: LockerverseCalculateTotalInput) {
    try {
      const parsed = parseCalculateTotalInput(input);
      const resolved = resolveCalculateTotalItems(parsed);
      const pricing = normalizePricingInput(parsed.pricing);
      return {
        catalog: resolved.catalog,
        items: resolved.items,
        pricing:
          pricing.includeServiceFee === undefined
            ? {
                ...pricing,
                includeServiceFee: resolved.catalog.widget.fields.serviceFee,
              }
            : pricing,
      };
    } catch (error) {
      if (error instanceof LockerverseSdkError) {
        throw error;
      }
      invalidInput(
        error instanceof TypeError
          ? error.message
          : "Checkout selection is invalid.",
        "calculate_total"
      );
    }
  }

  function normalizeSubmitPaymentInput(input: LockerverseSubmitPaymentInput) {
    try {
      const parsed = parseSubmitPaymentInput(input);
      const {
        items,
        pricing: pricingInput,
        quote: quoteInput,
      } = parsed.checkout;
      const pricing = normalizePricingInput(pricingInput);
      const quote = deepFreeze(quoteInput);
      const { submission } = parsed;
      if (quote.totalCents > 0 && !submission.confirmationToken) {
        invalidInput(
          "Stripe confirmation is required for a paid checkout.",
          "submit_payment",
          "payment_submit_failed"
        );
      }
      const email = submission.email.trim().toLowerCase();
      if (!email || email !== pricing.email) {
        invalidInput(
          "Payment email must match the current checkout quote.",
          "submit_payment"
        );
      }
      return {
        items,
        pricing,
        quote,
        submission: { ...submission, email },
      };
    } catch (error) {
      if (error instanceof LockerverseSdkError) {
        throw error;
      }
      invalidInput(
        "Lockerverse payment submission is invalid.",
        "submit_payment",
        "payment_submit_failed"
      );
    }
  }

  return {
    calculateTotal,
    async getPaymentStatus(paymentReference) {
      let normalizedReference: string;
      try {
        normalizedReference =
          parsePaymentReferenceInput(paymentReference).trim();
      } catch {
        invalidInput(
          "Provide a payment reference to recover its status.",
          "get_payment_status"
        );
      }
      const context = {
        code: "payment_status_failed" as const,
        message: "Lockerverse returned an invalid payment status.",
        operation: "get_payment_status" as const,
      };
      try {
        const response = await requestJson(
          `${checkoutBaseUrl}/${encodeURIComponent(normalizedReference)}/status`,
          undefined,
          parsePaymentResult,
          {
            ...context,
            message:
              "Unable to recover the Lockerverse payment status because the request failed.",
            report: false,
          },
          { ...context, report: false }
        );
        if (!response.ok) {
          throw new LockerverseSdkError({
            ...context,
            message: `Unable to recover the Lockerverse payment status (${response.response.status}).`,
            reportable: response.response.status >= 500,
            status: response.response.status,
          });
        }
        if (response.value.paymentReference !== normalizedReference) {
          throw new LockerverseSdkError({ ...context, reportable: true });
        }
        return clonePaymentResult(response.value);
      } catch (error) {
        failOperation(error, context);
      }
    },
    load,
    async submitPayment(input) {
      const { items, pricing, quote, submission } =
        normalizeSubmitPaymentInput(input);
      const context = {
        code: "payment_submit_failed" as const,
        message: "Lockerverse returned an invalid payment confirmation.",
        operation: "submit_payment" as const,
      };
      try {
        const response = await requestJson(
          `${checkoutBaseUrl}/confirm`,
          {
            body: JSON.stringify({
              confirmationToken: submission.confirmationToken,
              customFieldAnswers: submission.customFieldAnswers,
              email: submission.email,
              expectedAmountCents: quote.amountCents,
              expectedAppliedDiscount: quote.appliedDiscount,
              expectedConnectedAccountId: quote.payment.connectedAccountId,
              expectedDiscountableSubtotalCents:
                quote.discountableSubtotalCents,
              expectedDiscountCents: quote.discountCents,
              expectedServiceFeeCents: quote.serviceFeeCents,
              expectedSubtotalCents: quote.subtotalCents,
              expectedTipCents: quote.tipCents,
              expectedTotalCents: quote.totalCents,
              ...pricing,
              items,
              memberId: submission.memberId,
              merchSize: submission.merchSize,
              metadata: submission.metadata,
              paymentMethodConfiguration: submission.paymentMethodConfiguration,
              paymentReference: quote.paymentReference,
              phone: submission.phone,
              quoteContractVersion: 1,
              shipping: submission.shipping,
            }),
            headers: { "Content-Type": "application/json" },
            method: "POST",
          },
          parsePaymentResult,
          {
            ...context,
            message:
              "Unable to submit the Lockerverse payment because the request failed.",
            report: false,
          },
          { ...context, report: false },
          readPaymentServerCode
        );
        if (!response.ok) {
          const serverCode = response.errorValue;
          if (serverCode) {
            throw new LockerverseSdkError({
              ...context,
              message:
                serverCode === "PAYMENT_WIDGET_SALES_LIMIT_REACHED"
                  ? "This option is sold out."
                  : `Unable to submit the Lockerverse payment (${response.response.status}).`,
              reportable: false,
              serverCode,
              status: response.response.status,
            });
          }
          throw new LockerverseSdkError({
            ...context,
            message: `Unable to submit the Lockerverse payment (${response.response.status}).`,
            reportable: response.response.status >= 500,
            status: response.response.status,
          });
        }
        if (response.value.paymentReference !== quote.paymentReference) {
          throw new LockerverseSdkError({ ...context, reportable: true });
        }
        return clonePaymentResult(response.value);
      } catch (error) {
        failOperation(error, context, true);
      }
    },
  };
}
